// Downloads modal — lists what's in DOWNLOADS_DIR (APKs from CI plus
// any manually-dropped installers) and links each entry to /download/
// <file> for the actual byte stream. Auth is enforced on both the
// .list RPC and the byte-stream route by the outer middleware, so no
// extra gating needed here.

import { useEffect, useState } from "preact/hooks";
import "../styles/downloads.css";
import { Icon } from "../icons";
import { api, type DownloadFile } from "../lib/rpc";

interface DownloadsViewProps {
  open: boolean;
  onClose: () => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(ms: number): string {
  if (!ms) return "";
  const d = new Date(ms);
  const diff = Math.max(0, Date.now() - ms);
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) {
    // Today — show time
    return `today ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  }
  if (diff < 2 * day) return "yesterday";
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// Guess the platform label from the file extension so the user can
// see at-a-glance which entry is for which device. Coarse; falls
// back to the bare extension for anything unknown.
function platformLabel(name: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith(".apk")) return "Android";
  if (lower.endsWith(".exe")) return "Windows";
  if (lower.endsWith(".dmg")) return "macOS";
  if (lower.endsWith(".deb")) return "Linux · deb";
  if (lower.endsWith(".appimage")) return "Linux · AppImage";
  if (lower.endsWith(".zip")) return "Archive";
  const dot = lower.lastIndexOf(".");
  return dot >= 0 ? lower.slice(dot + 1) : "file";
}

export function DownloadsView({ open, onClose }: DownloadsViewProps) {
  const [files, setFiles] = useState<DownloadFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFiles(null);
    setError(null);
    api.downloadsList()
      .then((list) => setFiles(list))
      .catch((e) => setError(e?.message || "Failed to load downloads"));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="downloads-bg" onClick={onClose}>
      <div className="downloads-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Downloads">
        <header className="downloads-head">
          <div>
            <div className="downloads-title">Downloads</div>
            <div className="downloads-sub">Android, desktop, and installer builds</div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close">
            <Icon name="x" size={14} />
          </button>
        </header>

        <div className="downloads-body">
          {files === null && !error && (
            <p className="downloads-empty">loading…</p>
          )}
          {error && (
            <p className="downloads-empty" style={{ color: "var(--coral)" }}>{error}</p>
          )}
          {files && files.length === 0 && !error && (
            <p className="downloads-empty">
              No downloads yet. Builds land here automatically once CI finishes
              an Android job, or after a manual upload to the server's
              downloads directory.
            </p>
          )}
          {files && files.length > 0 && (
            <ul className="downloads-list">
              {files.map((f) => (
                <li key={f.name} className="downloads-row">
                  <div className="downloads-row-body">
                    <div className="downloads-row-name">{f.name}</div>
                    <div className="downloads-row-meta">
                      <span className="downloads-row-platform">{platformLabel(f.name)}</span>
                      <span> · </span>
                      <span>{formatSize(f.size)}</span>
                      <span> · </span>
                      <span>{formatTime(f.mtimeMs)}</span>
                    </div>
                  </div>
                  <a
                    className="btn btn-primary downloads-row-btn"
                    href={`/download/${encodeURIComponent(f.name)}`}
                    download={f.name}
                  >
                    <Icon name="download" size={13} />
                    <span>Download</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
